// markdownExportUtils.ts

export interface FileItem {
  file: File;
  order: number;
  path: string[];
}

export interface ProcessingStatus {
  isProcessing: boolean;
  progress: number;
  currentFile: string;
  error: string | null;
}

export type ProcessingCallback = (status: Partial<ProcessingStatus>) => void;

// Store mapping of image URLs to files
export const imageFileMap = new Map<string, File>();

/**
 * Extract filename from URL
 */
function getFilenameFromUrl(url: string): string {
  // Remove any URL parameters and get just the filename
  const cleanUrl = url.split("?")[0];
  const parts = cleanUrl.split("/");
  let filename = parts[parts.length - 1];

  // Decode URL encoding (e.g., %20 -> space)
  try {
    filename = decodeURIComponent(filename);
  } catch (e) {
    // If decoding fails, use the original filename
    console.warn(`Failed to decode filename: ${filename}`);
  }

  return filename;
}

/**
 * Check if a URL is an external link
 */
function isExternalLink(href: string): boolean {
  if (!href) return false;

  // Check if it's an absolute URL with protocol
  if (href.match(/^https?:\/\//i)) {
    return true;
  }

  // Check if it's a protocol-relative URL
  if (href.startsWith("//")) {
    return true;
  }

  // Check for other common protocols
  if (href.match(/^(ftp|mailto|tel|sms):/i)) {
    return true;
  }

  return false;
}

/**
 * Check if a URL is a local/internal link
 */
function isLocalLink(href: string): boolean {
  if (!href) return false;

  // If it's external, it's not local
  if (isExternalLink(href)) {
    return false;
  }

  // Check for relative links, anchors, or local file links
  // This includes .html files, relative paths, etc.
  if (
    href.match(/^[^:]*\.(html|htm|md|pdf|docx?)$/i) ||
    href.startsWith("./") ||
    href.startsWith("../") ||
    href.startsWith("#") ||
    (!href.includes("://") && href.includes(".html"))
  ) {
    return true;
  }

  return false;
}

/**
 * Convert HTML table to markdown
 */
function convertTableToMarkdown(table: HTMLTableElement): string {
  const rows = table.getElementsByTagName("tr");
  if (rows.length === 0) return "";

  let markdown = "\n\n";
  const allRowsData: string[][] = [];

  // First pass: collect all data
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const cells = Array.from(row.querySelectorAll("th, td"));
    const rowData: string[] = [];

    for (let j = 0; j < cells.length; j++) {
      const cell = cells[j];
      // Extract text content from cell
      let cellText = "";

      // Walk through all nodes in the cell
      const walker = document.createTreeWalker(
        cell,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        null
      );

      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeType === Node.TEXT_NODE) {
          cellText += node.textContent || "";
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          if (element.tagName === "BR") {
            cellText += " ";
          }
        }
      }

      // Clean up the text and escape pipe characters
      cellText = cellText.replace(/\s+/g, " ").trim();
      cellText = cellText.replace(/\|/g, "\\|"); // Escape pipes in content

      rowData.push(cellText);
    }

    allRowsData.push(rowData);
  }

  // Only check for <thead>, not <th> elements
  const thead = table.querySelector("thead");
  const hasHeaderRow = thead !== null;

  // Build the markdown table
  if (allRowsData.length > 0) {
    const numColumns = Math.max(...allRowsData.map((row) => row.length));

    let headerIndex = 0;
    if (hasHeaderRow && allRowsData.length > 0) {
      // Use first row as header (from thead)
      const headerRow = allRowsData[0];
      // Pad row if necessary
      while (headerRow.length < numColumns) {
        headerRow.push("");
      }
      markdown += "| " + headerRow.join(" | ") + " |\n";

      // Add separator row only when we have a header
      const separators = Array.from({ length: numColumns }, () => "---");
      markdown += "|" + separators.map((sep) => ` ${sep} `).join("|") + "|\n";

      headerIndex = 1;
    }

    // Add all data rows (starting from headerIndex)
    for (let i = headerIndex; i < allRowsData.length; i++) {
      const row = allRowsData[i];
      // Pad row if necessary
      while (row.length < numColumns) {
        row.push("");
      }
      markdown += "| " + row.slice(0, numColumns).join(" | ") + " |\n";
    }
  }

  markdown += "\n";
  return markdown;
}

/**
 * Process HTML element and convert to markdown
 */
function processElement(element: Element, allFiles: File[]): string {
  const tagName = element.tagName.toLowerCase();

  switch (tagName) {
    case "h1":
      return `\n# ${element.textContent?.trim()}\n\n`;
    case "h2":
      return `\n## ${element.textContent?.trim()}\n\n`;
    case "h3":
      return `\n### ${element.textContent?.trim()}\n\n`;
    case "h4":
      return `\n#### ${element.textContent?.trim()}\n\n`;
    case "h5":
      return `\n##### ${element.textContent?.trim()}\n\n`;
    case "h6":
      return `\n###### ${element.textContent?.trim()}\n\n`;

    case "p":
      return `${element.textContent?.trim()}\n\n`;

    case "br":
      return "\n";

    case "hr":
      return "\n---\n\n";

    case "blockquote":
      return `> ${element.textContent?.trim()}\n\n`;

    case "pre":
      const codeElement = element.querySelector("code");
      if (codeElement) {
        return `\`\`\`\n${codeElement.textContent}\n\`\`\`\n\n`;
      }
      return `\`\`\`\n${element.textContent}\n\`\`\`\n\n`;

    case "code":
      if (element.parentElement?.tagName.toLowerCase() !== "pre") {
        return `\`${element.textContent}\``;
      }
      return element.textContent || "";

    case "ul":
    case "ol":
      const listItems = element.querySelectorAll("li");
      let listMarkdown = "\n";
      listItems.forEach((li, index) => {
        const marker = tagName === "ul" ? "-" : `${index + 1}.`;
        listMarkdown += `${marker} ${li.textContent?.trim()}\n`;
      });
      return listMarkdown + "\n";

    case "table":
      return convertTableToMarkdown(element as HTMLTableElement);

    case "figure":
      if (element.classList.contains("link-to-page")) {
        const link = element.querySelector("a");
        if (link) {
          const href = link.getAttribute("href") || "";
          const text = link.textContent?.trim() || "";

          // Check if it's a local link
          if (isLocalLink(href)) {
            return `\n > ${text}\n\n`;
          } else {
            return `\n[${text}](${href})\n\n`;
          }
        }
      }

      // Handle figures with images
      const img = element.querySelector("img");
      if (img) {
        return processElement(img, allFiles) + "\n\n";
      }

      // Process children for other figures
      return processChildren(element, allFiles);

    case "strong":
    case "b":
      return `**${element.textContent?.trim()}**`;

    case "em":
    case "i":
      return `*${element.textContent?.trim()}*`;

    case "img":
      if (element.classList.contains("icon")) {
        return "";
      }

      const src = element.getAttribute("src") || "";

      const alt =
        element.getAttribute("alt") ||
        getFilenameFromUrl(src)
          .split(".")
          .slice(0, -1)
          .join(".")
          .replace(/[-_]+/g, " ") ||
        "Image";

      const filename = getFilenameFromUrl(src);

      const imageFile = allFiles.find((f) => f.name === filename);
      if (imageFile) {
        imageFileMap.set(filename, imageFile);
      }

      return `![${alt}](${filename})`;

    case "a":
      //   const href =
      //     (element as HTMLAnchorElement).href ||
      //     element.getAttribute("href") ||
      //     "";
      const text = element.textContent?.trim() || "";
      return text;

    //   // Check if it's a local link
    //   if (isLocalLink(href)) {
    //     // Convert to bold text instead of link
    //     return `**${text}**`;
    //   } else {
    //     // Keep external links as is
    //     return `[${text}](${href})`;
    //}

    case "span":
      // Handle special span classes
      if (element.classList.contains("user")) {
        return element.textContent || "";
      }
      if (element.classList.contains("icon")) {
        return element.textContent || "";
      }
      // For other spans, process their children
      return processChildren(element, allFiles);

    case "div":
    case "section":
    case "article":
    case "main":
      // For container elements, process children and add spacing
      return processChildren(element, allFiles) + "\n\n";

    default:
      // For unknown elements, just process their children
      return processChildren(element, allFiles);
  }
}

/**
 * Process all children of an element
 */
function processChildren(element: Element, allFiles: File[]): string {
  let result = "";

  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent?.trim();
      if (text) {
        result += text;
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      result += processElement(child as Element, allFiles);
    }
  }

  return result;
}

/**
 * Extract text content from HTML and convert it to Markdown format
 */
export const extractTextFromHTML = (html: string, allFiles: File[]): string => {
  // Parse HTML using browser's DOMParser
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Remove script and style elements
  doc.querySelectorAll("script, style").forEach((el) => el.remove());

  // Get the body content or use the document element
  const content = doc.body || doc.documentElement;

  // Find and process the header separately to avoid duplication
  const header = content.querySelector("header");
  const pageTitle =
    header?.querySelector(".page-title")?.textContent?.trim() || "";

  // remove any standalone H1 that matches the page title to avoid duplication
  if (pageTitle) {
    const h1Elements = content.querySelectorAll("h1");
    h1Elements.forEach((h1) => {
      if (h1.textContent?.trim() === pageTitle) {
        h1.remove();
      }
    });
  }

  let markdown = "";
  markdown += processChildren(content, allFiles);
  markdown = markdown.replace(/\n{3,}/g, "\n\n").trim();
  return markdown;
};

/**
 * Process markdown to convert local links to bold text
 */
export const processMarkdownLinks = (markdown: string): string => {
  // Regex to match markdown links: [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

  return markdown.replace(linkRegex, (match, text, url) => {
    if (isLocalLink(url)) {
      // Convert to bold text
      return `**${text}**`;
    } else {
      // Keep external links as is
      return match;
    }
  });
};

/**
 * Process files in chunks to manage memory
 */
export const processFilesInChunks = async (
  files: FileItem[],
  allFiles: File[],
  onProgress?: ProcessingCallback,
  chunkSize: number = 10
): Promise<string> => {
  let fullMarkdown = "";
  const totalFiles = files.length;

  // Clear image map before processing
  imageFileMap.clear();

  for (let i = 0; i < totalFiles; i += chunkSize) {
    const chunk = files.slice(i, Math.min(i + chunkSize, totalFiles));

    // Process chunk
    const chunkResults = await Promise.all(
      chunk.map(async (item, chunkIndex) => {
        const currentIndex = i + chunkIndex;

        onProgress?.({
          currentFile: item.file.name,
          progress: (currentIndex / totalFiles) * 100,
        });

        try {
          const text = await item.file.text();
          const markdown = extractTextFromHTML(text, allFiles);

          // Add file header with title and breadcrumbs
          const title = item.path[item.path.length - 1] || item.file.name;
          const breadcrumbs = item.path.join(" > ");

          // Create header with title as H1 and breadcrumbs in smaller text below
          const header = `\n\n# ${title}\n\n*${breadcrumbs}*\n`;
          return header + markdown + "\n\n\\pagebreak";
        } catch (error) {
          console.error(`Error processing ${item.file.name}:`, error);
          return `\n\n---\n\n# Error processing ${item.file.name}\n\n`;
        }
      })
    );

    fullMarkdown += chunkResults.join("");

    // Allow UI to update
    await new Promise((resolve) => setTimeout(resolve, 10));
  }

  return fullMarkdown;
};

/**
 * Generate complete markdown document with header
 */
export const generateMarkdownDocument = async (
  parentFileName: string,
  files: FileItem[],
  allFiles: File[],
  onProgress?: ProcessingCallback
): Promise<string> => {
  const sortedFiles = [...files].sort((a, b) => a.order - b.order);
  let markdown = await processFilesInChunks(sortedFiles, allFiles, onProgress);

  // Process the final markdown to convert any remaining local links
  markdown = processMarkdownLinks(markdown);

  return markdown;
};

/**
 * Get the image file map (for PDF generation)
 */
export const getImageFileMap = (): Map<string, File> => {
  return imageFileMap;
};

/**
 * Download markdown content as a file
 */
export const downloadMarkdown = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".md") ? filename : `${filename}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
